import { AfterViewInit, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppConstants } from 'src/app/core/constants/appconstants';
import { ApiConstants } from 'src/app/core/constants/api-path.constants';
import { ApiService } from 'src/app/core/services/api.service';
import { ServiceLocalStorage } from 'src/app/core/services/local-storage.service';

declare let L: any;
declare let google: any;

@Component({
  selector: 'app-unreachable-vehicle-map-view',
  templateUrl: './unreachable-vehicle-map-view.component.html',
  styleUrls: ['./unreachable-vehicle-map-view.component.scss']
})
export class UnreachableVehicleMapViewComponent implements AfterViewInit {
  @ViewChild('mapContainer') mapContainer: ElementRef;

  map: any;
  mapType: string;
  showGoogleMap = false;
  isOsm = false;
  googleMapInitialized = false;
  mapLoadAttempts = 0;
  maxMapLoadAttempts = 50;

  constructor(
    public dialogRef: MatDialogRef<UnreachableVehicleMapViewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly localStorage: ServiceLocalStorage,
    private readonly apiService: ApiService
  ) {
    // this.mapType = this.localStorage.getMapType();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.resolveMapTypeAndInit(), 100);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  /** Uses client master-config flag (same as panic-action-notification). */
  private resolveMapTypeAndInit(): void {
    const clientCode = localStorage.getItem('clientCode');
    if (!clientCode) {
      this.initMapByClientFlag();
      return;
    }
    this.apiService.doGetRequestWithResponse(
      ApiConstants.getMapType + clientCode,
      success => {
        if (success.status === 200) {
          this.localStorage.setGoogleMapTypeEnabled(!!success.body?.isGoogleMapEnabled);
        }
        this.initMapByClientFlag();
      },
      () => this.initMapByClientFlag()
    );
  }

  private initMapByClientFlag(): void {
    this.showGoogleMap = this.localStorage.isSetGoogleMapTypeEnabled();
    this.isOsm = !this.showGoogleMap;
    this.mapType = this.isOsm ? AppConstants.OSRM : 'GOOGLE';
    this.googleMapInitialized = false;
    this.mapLoadAttempts = 0;
    this.initMap();
  }

  private initMap(): void {
    const latitude = Number(this.data?.latitude);
    const longitude = Number(this.data?.longitude);
    if (!this.mapContainer?.nativeElement || Number.isNaN(latitude) || Number.isNaN(longitude)) {
      return;
    }

    const vehicleLabel = this.data?.vehicleRegistrationNumber || this.data?.tripCode || 'Vehicle';

    if (this.isOsm) {
      this.initOsmMap(latitude, longitude, vehicleLabel);
      return;
    }

    this.loadgmap(latitude, longitude, vehicleLabel);
  }

  private initOsmMap(latitude: number, longitude: number, vehicleLabel: string): void {
    this.map = L.map(this.mapContainer.nativeElement, {
      dragging: true,
      zoomControl: true
    }).setView([latitude, longitude], 15);

    L.tileLayer(AppConstants.OSM_TILE, {
      attribution: AppConstants.OSM_CONTRIBUTORS
    }).addTo(this.map);

    L.marker([latitude, longitude], {
      icon: L.icon({
        iconUrl: './assets/images/livetracking/vehicle/258BE8.png',
        iconSize: [45, 40],
        iconAnchor: [15, 20]
      })
    }).addTo(this.map).bindPopup(vehicleLabel);

    setTimeout(() => this.map?.invalidateSize?.(), 200);
  }

  private loadgmap(latitude: number, longitude: number, vehicleLabel: string): void {
    if (!this.showGoogleMap || this.isOsm) {
      return;
    }
    if (this.googleMapInitialized && this.map?.setCenter) {
      return;
    }
    if (typeof google === 'undefined' || (!google.maps?.Map && !google.maps?.importLibrary)) {
      this.scheduleGoogleMapLoadRetry(latitude, longitude, vehicleLabel);
      return;
    }
    this.initGoogleMapAsync(latitude, longitude, vehicleLabel);
  }

  private scheduleGoogleMapLoadRetry(latitude: number, longitude: number, vehicleLabel: string): void {
    if (++this.mapLoadAttempts >= this.maxMapLoadAttempts) {
      return;
    }
    setTimeout(() => this.loadgmap(latitude, longitude, vehicleLabel), 100);
  }

  private async initGoogleMapAsync(latitude: number, longitude: number, vehicleLabel: string): Promise<void> {
    if (this.googleMapInitialized && this.map?.setCenter) {
      return;
    }
    try {
      let MapClass = google.maps?.Map;
      if (google.maps?.importLibrary) {
        const mapsLib = await google.maps.importLibrary('maps') as any;
        MapClass = mapsLib.Map;
      }
      if (!MapClass) {
        this.scheduleGoogleMapLoadRetry(latitude, longitude, vehicleLabel);
        return;
      }
      this.map = new MapClass(this.mapContainer.nativeElement, {
        center: { lat: latitude, lng: longitude },
        zoom: 15,
        draggable: true,
        zoomControl: true,
        gestureHandling: 'greedy'
      });
      this.googleMapInitialized = true;
      const marker = new google.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map: this.map,
        title: vehicleLabel,
        icon: {
          url: './assets/images/livetracking/vehicle/258BE8.png',
          scaledSize: new google.maps.Size(45, 40),
          anchor: new google.maps.Point(15, 20)
        }
      });
      const infoWindow = new google.maps.InfoWindow({
        content: `<b>${this.escapeHtml(vehicleLabel)}</b>`
      });
      google.maps.event.addListener(marker, 'click', () => {
        infoWindow.open(this.map, marker);
      });
      this.createOsmLikeLabel(this.map, { lat: latitude, lng: longitude }, vehicleLabel, 'top');
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        google.maps.event.trigger(this.map, 'resize');
        this.map.setCenter({ lat: latitude, lng: longitude });
      });
    } catch (error) {
      console.error('Google Map init failed', error);
      this.scheduleGoogleMapLoadRetry(latitude, longitude, vehicleLabel);
    }
  }

  // private initGoogleMap(latitude: number, longitude: number, vehicleLabel: string): void {
  //   this.map = new google.maps.Map(this.mapContainer.nativeElement, { ... });
  // }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /** Leaflet-style permanent tooltip matching OSM bindPopup(vehicleLabel). */
  private createOsmLikeLabel(
    map: any,
    position: { lat: number; lng: number },
    text: string,
    placement: 'left' | 'top' = 'top'
  ): any {
    if (!map || !google?.maps?.OverlayView || !text) {
      return null;
    }
    const Overlay = google.maps.OverlayView;
    class OsmLikeLabel extends Overlay {
      div: HTMLDivElement | null = null;
      pos: any;
      labelText: string;
      labelPlacement: 'left' | 'top';

      constructor(pos: any, labelText: string, labelPlacement: 'left' | 'top') {
        super();
        this.pos = pos;
        this.labelText = labelText;
        this.labelPlacement = labelPlacement;
      }

      onAdd() {
        const div = document.createElement('div');
        div.className = this.labelPlacement === 'left'
          ? 'gm-leaflet-tooltip gm-leaflet-tooltip-left'
          : 'gm-leaflet-tooltip gm-leaflet-tooltip-top';
        const safe = String(this.labelText || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        div.innerHTML = `<b>${safe}</b>`;
        this.div = div;
        this.getPanes()?.overlayMouseTarget?.appendChild(div);
      }

      draw() {
        if (!this.div) return;
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(this.pos);
        if (!point) return;
        this.div.style.left = `${point.x}px`;
        this.div.style.top = this.labelPlacement === 'left'
          ? `${point.y - 22}px`
          : `${point.y - 20}px`;
      }

      onRemove() {
        if (this.div?.parentNode) {
          this.div.parentNode.removeChild(this.div);
        }
        this.div = null;
      }
    }

    const overlay = new OsmLikeLabel(
      new google.maps.LatLng(position.lat, position.lng),
      text,
      placement
    );
    overlay.setMap(map);
    return overlay;
  }
}
